import createNotification, {
  ERROR_NOTIFICATION,
  SUCCESS_NOTIFICATION,
} from "../../Notifications";

import { DEFAULT_CHEMICAL_CURRENCY_CODE } from "../../../constants/chemicals.constants";

import { extractPlantId } from "../../../utils/Utils";
import { updatePlantId } from "../../../utils/Utils";

import {
  addCustomChemicalToUserLibrary,
  deletePlants,
  deleteUserChemicalLibrary,
  getPlantChemicalLibrary,
  getUserChemicalLibrary,
  getUserChemicalUsage,
  updatePlantChemicalLibrary,
  updateUserChemicalLibrary,
} from "../../../network/cipAPI";

import {
  storePlantChemicalLibrary,
  storeUserChemicalLibrary,
} from "../../../Store/TemplateStore/template.action";

let chemicalLibraryHydrationPromise = null;

const getChemicalLibraryErrorMessage = (response = {}, t) => {
  const key = response?.detail?.key || response?.key;

  if (key === "DUPLICATE_ENTRY") {
    return t("chemicalLibrary.duplicateEntryError");
  }

  return response?.detail?.error || response?.error;
};

const buildCustomChemicalPayload = (item) => ({
  chemical: item.chemical,
  price: Number(item.price),
  unit: item.unit,
  currency: item.currency || DEFAULT_CHEMICAL_CURRENCY_CODE,
  category: item.category,
  symbol: item.symbol,
  display_name: item.display_name,
  bulk_density: Number(item.bulk_density),
  bulk_concentration: Number(item.bulk_concentration),
  // Refer to ChemicalVisibilityToggleRenderer for details
  // inherit_to_plants: true,
});

const buildUpdateChemicalPayload = (item) => ({
  chem_id: item.chem_id,
  price: Number(item.price),
  unit: item.unit,
});

const isCustomChemicalCreate = (item, isUserScope) =>
  isUserScope && !item?.chem_id;

const sortDefaultFirst = (list = []) =>
  [...list].sort(
    (a, b) => Number(Boolean(a?.is_custom)) - Number(Boolean(b?.is_custom)),
  );

const resolveChemicalLibraryUpdateApi = (
  chemicalDataForUpdate,
  isUserScope,
  isMakeDefault,
  hasOnlyCustomCreates,
) => {
  if (hasOnlyCustomCreates) {
    return addCustomChemicalToUserLibrary;
  }

  if (isUserScope || isMakeDefault) {
    return updateUserChemicalLibrary;
  }

  return updatePlantChemicalLibrary;
};

export const fetchChemicalLibraryData = async (t, isUserScope) => {
  try {
    const plantId = extractPlantId();
    const response =
      plantId && !isUserScope
        ? await getPlantChemicalLibrary()
        : await getUserChemicalLibrary();

    return response?.status === 200
      ? sortDefaultFirst(response.data || [])
      : [];
  } catch (error) {
    createNotification(
      ERROR_NOTIFICATION,
      t("notificationStrings.somethingWrong"),
      "",
    );
    return [];
  }
};

export const ensureChemicalLibraryHydrated = async (dispatch, t) => {
  if (chemicalLibraryHydrationPromise) {
    return chemicalLibraryHydrationPromise;
  }

  chemicalLibraryHydrationPromise = (async () => {
    try {
      const chemicalData = await fetchChemicalLibraryData(t, false);
      const plantId = extractPlantId();

      dispatch(storePlantChemicalLibrary(chemicalData));
      if (!plantId) {
        dispatch(storeUserChemicalLibrary(chemicalData));
      }
    } finally {
      chemicalLibraryHydrationPromise = null;
    }
  })();

  return chemicalLibraryHydrationPromise;
};

export const checkUserChemicalUsage = async (chemId) => {
  if (!chemId) {
    return [];
  }

  try {
    const response = await getUserChemicalUsage(chemId);
    if (response?.status !== 200) {
      return [];
    }

    const usageData = response?.data ?? response;
    if (Array.isArray(usageData)) {
      return usageData;
    }

    if (Array.isArray(usageData?.projects)) {
      return usageData.projects;
    }

    return [];
  } catch (error) {
    return [];
  }
};

export const updateChemicalLibraryData = async (
  chemicalDataForUpdate,
  t,
  isUserScope,
  isMakeDefault = false,
  showNotification = true,
) => {
  const fallbackErrorMessage = t("notificationStrings.somethingWrong");
  const hasOnlyCustomCreates =
    Array.isArray(chemicalDataForUpdate) &&
    chemicalDataForUpdate.length > 0 &&
    chemicalDataForUpdate.every((item) =>
      isCustomChemicalCreate(item, isUserScope),
    );

  try {
    const chemicals = chemicalDataForUpdate.map((item) => {
      if (isCustomChemicalCreate(item, isUserScope)) {
        return buildCustomChemicalPayload(item);
      }

      const isUserCustomEdit = isUserScope && Boolean(item?.is_custom);

      const basePayload = buildUpdateChemicalPayload(item);

      // Refer to ChemicalVisibilityToggleRenderer for details
      // const basePayload = {
      //   ...buildUpdateChemicalPayload(item),
      //   ...(isUserScope && item?.chem_id
      //     ? { inherit_to_plants: item?.inherit_to_plants !== false }
      //     : {}),
      // };

      if (isUserCustomEdit) {
        const payload = { ...basePayload };

        if (item?.bulk_density !== undefined && item?.bulk_density !== null) {
          payload.bulk_density = Number(item.bulk_density);
        }

        if (
          item?.bulk_concentration !== undefined &&
          item?.bulk_concentration !== null
        ) {
          payload.bulk_concentration = Number(item.bulk_concentration);
        }

        if (item?.display_name !== undefined) {
          payload.display_name = item.display_name;
        }

        return payload;
      }

      return basePayload;
    });

    const payload = { chemicals };

    const updateChemicalLibrary = resolveChemicalLibraryUpdateApi(
      chemicalDataForUpdate,
      isUserScope,
      isMakeDefault,
      hasOnlyCustomCreates,
    );

    const response = await updateChemicalLibrary(payload);
    const apiErrorMessage = getChemicalLibraryErrorMessage(response, t);

    if (!response || response?.status !== 200 || apiErrorMessage) {
      throw new Error(apiErrorMessage || fallbackErrorMessage);
    }

    const successMessage = isMakeDefault
      ? t("chemicalLibrary.dataSetAsDefaultSuccessfully")
      : t("chemicalLibrary.dataUpdatedSuccessfully");

    if (showNotification) {
      createNotification(SUCCESS_NOTIFICATION, successMessage, "");
    }

    if (Array.isArray(response?.data)) {
      return sortDefaultFirst(response.data);
    }

    if (Array.isArray(response?.data?.chemicals)) {
      return sortDefaultFirst(response.data.chemicals);
    }

    return [];
  } catch (error) {
    const errorMsg =
      error?.message ||
      getChemicalLibraryErrorMessage(error, t) ||
      fallbackErrorMessage;

    if (showNotification) {
      createNotification(ERROR_NOTIFICATION, errorMsg, "");
    }
    return null;
  }
};

export const checkChemicalUsageData = async (chemId, t) => {
  try {
    const response = await getUserChemicalUsage(chemId);
    const usageList = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];

    return usageList;
  } catch (error) {
    createNotification(
      ERROR_NOTIFICATION,
      t("notificationStrings.somethingWrong"),
      "",
    );
    return null;
  }
};

export const deleteChemicalLibraryData = async (chemId, t) => {
  try {
    await deleteUserChemicalLibrary({ chem_ids: [chemId] });
    createNotification(
      SUCCESS_NOTIFICATION,
      t("chemicalLibrary.chemicalDeletedSuccessfully"),
      "",
    );
    return true;
  } catch (error) {
    createNotification(
      ERROR_NOTIFICATION,
      t("notificationStrings.somethingWrong"),
      "",
    );
    return false;
  }
};

export const deleteUsagePlant = async (plantId, t) => {
  if (!plantId) {
    return {
      success: false,
      errorMessage: t("notificationStrings.somethingWrong"),
    };
  }

  try {
    updatePlantId(plantId);
    const response = await deletePlants();

    if (response?.status === 200) {
      createNotification(
        SUCCESS_NOTIFICATION,
        t("chemicalLibrary.plantDeletedSuccessfully"),
        "",
      );
      return { success: true };
    }

    return {
      success: false,
      errorMessage: t("notificationStrings.somethingWrong"),
    };
  } catch (error) {
    return {
      success: false,
      errorMessage: t("notificationStrings.somethingWrong"),
    };
  }
};
